'use client'

import React, { useState, useEffect, useMemo } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { apiGetPopularDeals } from '@/apis/page_optimization'
import { getBaseImageUrl, getMerchantHref, getCurrencySymbol, discardHTMLTags, calculateOfferDuration, extractPromoDiscountTag } from '@/constants/hooks'
import { TrendingUp, Zap, Clock, ShoppingBag, ChevronRight, ArrowRight, Eye, BadgeCheck, Star } from 'lucide-react'
import Theme18OfferLink from './Theme18OfferLink'

const ProductDetailsModal = dynamic(() => import('./ProductDetailsModal'), { ssr: false })

const C = { PRIMARY: '#F8F246', SECONDARY: '#8C5826', YELLOW: '#FF9934', BROWN: '#8C5826', LIGHT: '#fffbf0', WARM_BG: '#fffaf5', DARK: '#000000' }

// Soft pastel palettes for tags and highlights matching Theme-18
const tagPalettes = {
    orange: { bg: '#FFF4E0', color: '#FF9934', border: '#FFF0D0' },
    teal: { bg: '#E0F7F9', color: '#2CBBC3', border: '#D0F2F5' },
    yellow: { bg: '#FFF9D6', color: '#B89000', border: '#FFF5BD' },
    green: { bg: '#E8F5E9', color: '#2e7d32', border: '#C8E6C9' },
    purple: { bg: '#EDE7F6', color: '#673ab7', border: '#D1C4E9' },
    red: { bg: '#FFEBEE', color: '#c62828', border: '#FFCDD2' }
}

const getExpiryTag = (endDate: string) => {
    const duration = calculateOfferDuration(endDate)
    if (duration === 'Offer expired') return { text: 'Expired', ...tagPalettes.red }
    if (duration === 'Last day') return { text: 'Last Day', ...tagPalettes.orange }
    return { text: duration, ...tagPalettes.teal }
}

const getDiscountTag = (title: string) => {
    const tag = extractPromoDiscountTag(title)
    if (!tag) return null
    if (tag.includes('%')) return { text: tag, ...tagPalettes.orange }
    if (tag === 'BOGO') return { text: 'BOGO', ...tagPalettes.teal }
    if (tag === 'Free Shipping' || tag === 'Free Delivery') return { text: 'Free Ship', ...tagPalettes.green }
    if (tag === 'Free Gift') return { text: 'Free Gift', ...tagPalettes.purple }
    return { text: tag, ...tagPalettes.yellow }
}

const injectLocalStyles = () => {
    if (typeof document === 'undefined') return
    const styleId = 'pet-trending-premium-styles'
    if (document.getElementById(styleId)) return
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
        @keyframes softEntrance {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .trend-card-anim {
            animation: softEntrance 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .card-shadow-hover {
            box-shadow: 0 4px 20px rgba(140, 88, 38, 0.04);
            transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
        }
        .card-shadow-hover:hover {
            transform: translateY(-6px);
            box-shadow: 0 0 20px rgba(248, 242, 70, 0.3), 0 16px 36px rgba(140, 88, 38, 0.08);
            border-color: #F8F246 !important;
        }
        .card-shadow-hover:hover .trend-title {
            color: #8C5826 !important;
        }
        .bg-pattern {
            background-image: radial-gradient(#8c5826 1px, transparent 1px);
            background-size: 20px 20px;
        }
    `
    document.head.appendChild(style)
}

interface Props { companyId: string; mer_slug: string; mer_slug_type: string; domain?: string }

const PetCareTrendingProducts = ({ companyId, mer_slug, mer_slug_type, domain = '' }: Props) => {
    const [products, setProducts] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('all')
    const [selectedProduct, setSelectedProduct] = useState<any>(null)
    const [favorites, setFavorites] = useState<Set<string>>(new Set())
    const currency = getCurrencySymbol()

    useEffect(() => {
        injectLocalStyles()
        apiGetPopularDeals(companyId)
            .then(r => r?.data?.offers && setProducts(r.data.offers.slice(0, 8)))
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [companyId])

    const toggleFavorite = (id: string) => {
        setFavorites(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    // Automatically detect and extract dynamic category tabs based on products
    const tabs = useMemo(() => {
        const list = [{ id: 'all', label: 'All Products', icon: TrendingUp }]
        const counts: Record<string, number> = { food: 0, toys: 0, health: 0 }

        products.forEach(p => {
            const title = (p.offer?.offer_title || '').toLowerCase()
            if (title.includes('food') || title.includes('treat')) counts.food++
            if (title.includes('toy') || title.includes('play')) counts.toys++
            if (title.includes('health') || title.includes('care') || title.includes('vet')) counts.health++
        })

        if (counts.food > 0) list.push({ id: 'food', label: 'Food & Treats', icon: ShoppingBag })
        if (counts.toys > 0) list.push({ id: 'toys', label: 'Toys & Play', icon: Zap })
        if (counts.health > 0) list.push({ id: 'health', label: 'Health & Care', icon: Star })

        return list
    }, [products])

    const filteredProducts = useMemo(() => {
        return products.filter(productItem => {
            const title = (productItem.offer?.offer_title || '').toLowerCase()
            if (activeTab === 'all') return true
            if (activeTab === 'food') return title.includes('food') || title.includes('treat')
            if (activeTab === 'toys') return title.includes('toy') || title.includes('play')
            if (activeTab === 'health') return title.includes('health') || title.includes('care') || title.includes('vet')
            return true
        })
    }, [products, activeTab])

    if (loading) return (
        <section className="py-16 px-4" style={{ background: C.WARM_BG }}>
            <div className="max-w-7xl mx-auto animate-pulse">
                <div className="h-4 w-32 bg-gray-200 mx-auto rounded-full mb-3" />
                <div className="h-8 w-64 bg-gray-200 mx-auto rounded-xl mb-8" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-80 bg-white rounded-3xl border border-gray-100 p-4 space-y-4">
                            <div className="h-40 bg-gray-100 rounded-2xl w-full" />
                            <div className="h-4 bg-gray-100 rounded w-2/3" />
                            <div className="h-4 bg-gray-100 rounded w-1/2" />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )

    return (
        <section className="py-10 md:py-16 px-4 relative overflow-hidden" style={{ background: C.WARM_BG }}>
            {/* Soft decorative background circles */}
            <div className="absolute top-10 left-10 w-96 h-96 rounded-full opacity-[0.15] blur-3xl pointer-events-none" style={{ background: C.PRIMARY }} />
            <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full opacity-[0.1] blur-3xl pointer-events-none" style={{ background: C.SECONDARY }} />

            {/* Delicate dot pattern in background */}
            <div className="absolute inset-0 opacity-[0.02] bg-pattern pointer-events-none" />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header Section */}
                <div className="text-center mb-6 md:mb-10">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2" style={{ color: '#8C5826' }}>
                        Popular Picks
                    </p>
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-gray-900 mb-3">
                        Trending <span style={{ color: '#8C5826' }}>Pet Products</span>
                    </h2>
                    <p className="text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
                        Treat your furry best friend to our most-loved and highly-rated products of the week.
                    </p>
                </div>

                {/* Categories Tab Selector */}
                {tabs.length > 1 && (
                    <div className="flex flex-wrap justify-center gap-2 mb-6">
                        {tabs.map(tab => {
                            const Icon = tab.icon
                            const isActive = activeTab === tab.id
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-300 hover:scale-105 cursor-pointer"
                                    style={{
                                        background: isActive ? C.PRIMARY : '#ffffff',
                                        color: isActive ? C.BROWN : '#6b7280',
                                        border: isActive ? `1.5px solid ${C.PRIMARY}` : '1.5px solid #e5e7eb',
                                        boxShadow: isActive ? '0 8px 20px rgba(248, 242, 70, 0.3)' : 'none'
                                    }}
                                >
                                    <Icon size={13} />
                                    {tab.label}
                                </button>
                            )
                        })}
                    </div>
                )}

                {/* Product Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                    {filteredProducts.map((productItem, index) => {
                        const product = productItem.offer
                        const merchant = productItem.merchant
                        const targetDomain = domain || (typeof window !== 'undefined' ? window.location.hostname : '')
                        const rawImg = product?.product_image || merchant?.merchant_logo
                        const imageUrl = rawImg ? getBaseImageUrl(targetDomain, rawImg, '') : ''

                        const productHref = getMerchantHref(merchant, mer_slug, mer_slug_type)
                        const expiryTag = product.end_date ? getExpiryTag(product.end_date) : null
                        const discountTag = getDiscountTag(product.offer_title || '')

                        return (
                            <div
                                key={productItem.id || index}
                                className="trend-card-anim group flex flex-col justify-between bg-white rounded-2xl md:rounded-3xl border border-gray-100 p-3 md:p-4 card-shadow-hover"
                                style={{ animationDelay: `${index * 0.05}s` }}
                            >
                                <div className="relative">
                                    {/* Top badges row */}
                                    <div className="absolute top-2 left-2 right-2 z-20 flex justify-between items-start pointer-events-none">
                                        {/* Discount tag — top left */}
                                        {discountTag && (
                                            <span
                                                className="px-2.5 py-1 rounded-full text-[9px] font-black tracking-wider uppercase border shadow-2xs"
                                                style={{ background: '#FFF4E0', color: '#FF9934', borderColor: '#FFF0D0' }}
                                            >
                                                {discountTag.text}
                                            </span>
                                        )}

                                        {/* Expiry tag — top right */}
                                        {expiryTag && (
                                            <div
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold border"
                                                style={{ background: expiryTag.bg, color: expiryTag.color, borderColor: expiryTag.border }}
                                            >
                                                <Clock size={9} />
                                                {expiryTag.text}
                                            </div>
                                        )}
                                    </div>

                                    {/* Product Image Container */}
                                    <div className="relative h-40 sm:h-52 w-full rounded-xl md:rounded-2xl mb-3 overflow-hidden bg-gradient-to-br from-[#fffdfa] via-[#fcfbf9] to-[#f7f5f0] border border-[#f5f2eb] flex items-center justify-center p-3" style={{ background: '#fcfbf9' }}>
                                        <div className="absolute inset-0 opacity-[0.03] bg-pattern pointer-events-none" />
                                        {imageUrl ? (
                                            <Image
                                                src={imageUrl}
                                                alt=""
                                                fill
                                                className="object-contain p-4 group-hover:scale-110 transition-transform duration-500 drop-shadow-xs"
                                                sizes="(max-width: 768px) 50vw, 25vw"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <ShoppingBag size={36} className="text-[#8C5826]/30" />
                                        )}
                                    </div>

                                    {/* Store/Merchant */}
                                    {merchant?.merchant_name && (
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                                {merchant.merchant_name}
                                            </span>
                                        </div>
                                    )}

                                    {/* Product Title */}
                                    <Theme18OfferLink
                                        unique_id={product.unique_id}
                                        outUrl={product.url || productHref}
                                        merchantHref={productHref}
                                        domain={domain}
                                        couponCode={product.coupon_code || undefined}
                                        customClass="block"
                                    >
                                        <h3
                                            className="trend-title text-sm font-bold text-center line-clamp-2 mb-2 transition-colors duration-300"
                                            style={{ color: C.DARK }}
                                        >
                                            {product.offer_title ? discardHTMLTags(product.offer_title) : 'Premium Pet Product'}
                                        </h3>
                                    </Theme18OfferLink>

                                </div>

                                {/* Price and CTA Row */}
                                <div className="border-t border-gray-100 pt-3 mt-2 flex flex-col gap-2.5">
                                    <div className="flex items-center justify-between">
                                        <div className="flex flex-col">
                                            {product.original_price ? (
                                                <>
                                                    <span className="text-base font-black text-gray-900">
                                                        {currency}{product.sale_price || product.original_price}
                                                    </span>
                                                    {Number(product.original_price) > Number(product.sale_price) && (
                                                        <span className="text-[10px] text-gray-400 line-through">
                                                            {currency}{product.original_price}
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-xs font-bold text-green-600">Exclusive Deal</span>
                                            )}
                                        </div>

                                        {/* View Details (Theme 15 style) */}
                                        <button
                                            onClick={() => setSelectedProduct(productItem)}
                                            className="inline-flex items-center gap-1 text-xs text-[#8C5826] hover:text-[#5e3814] cursor-pointer hover:underline font-bold transition-colors"
                                        >
                                            <Eye size={12} />
                                            <span>View Details</span>
                                        </button>
                                    </div>

                                    <Theme18OfferLink
                                        unique_id={product.unique_id}
                                        outUrl={product.url || productHref}
                                        merchantHref={productHref}
                                        domain={domain}
                                        couponCode={product.coupon_code || undefined}
                                        customClass="w-full block"
                                    >
                                        <div
                                            className="w-full flex items-center justify-center gap-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 hover:scale-[1.02] shadow-sm cursor-pointer"
                                            style={{ background: C.PRIMARY, color: C.BROWN }}
                                        >
                                            <span>{product.coupon_code ? 'Show Code' : (product?.offer_type?.name === 'product' ? 'Buy Now' : 'Get Deal')}</span>
                                            <ArrowRight size={12} />
                                        </div>
                                    </Theme18OfferLink>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {/* Empty State */}
                {filteredProducts.length === 0 && !loading && (
                    <div className="text-center py-16 bg-white rounded-3xl border border-gray-100">
                        <ShoppingBag size={40} className="mx-auto text-gray-300 mb-3" />
                        <h3 className="text-lg font-bold text-gray-700 mb-1">No products found</h3>
                        <p className="text-sm text-gray-400 mb-4">Try choosing another category tab above.</p>
                        <button
                            onClick={() => setActiveTab('all')}
                            className="px-5 py-2 rounded-full text-xs font-bold transition-all cursor-pointer"
                            style={{ background: C.PRIMARY, color: C.BROWN }}
                        >
                            View All
                        </button>
                    </div>
                )}

                {/* Bottom View All Button */}
                {filteredProducts.length > 0 && (
                    <div className="text-center mt-8 md:mt-12">
                        <Link
                            href="/all-products"
                            className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-bold transition-all duration-300 hover:scale-105"
                            style={{ background: '#ffffff', color: C.BROWN, border: '1.5px solid #e5e7eb', boxShadow: '0 4px 12px rgba(140, 88, 38, 0.03)' }}
                        >
                            View All Trending Products
                            <ChevronRight size={15} />
                        </Link>
                    </div>
                )}
            </div>

            {/* Product Details Modal popup */}
            {selectedProduct && (
                <ProductDetailsModal
                    product={selectedProduct.offer}
                    merchantName={selectedProduct.merchant?.merchant_name || 'Pet Store'}
                    merchantLogo={selectedProduct.merchant?.merchant_logo || ''}
                    domain={typeof window !== 'undefined' ? window.location.hostname : ''}
                    isOpen={true}
                    onClose={() => setSelectedProduct(null)}
                />
            )}
        </section>
    )
}

export default PetCareTrendingProducts